/**
 * About Page Component
 * Renders the Chaggerheart vision statement for public viewing
 */

import React from 'react';
import { Link } from 'react-router-dom';
import IconDaggerheart from '../common/IconDaggerheart';

export default function About() {
  return (
    <div className="min-h-screen bg-surface-1 text-gray-100">
      {/* Navigation — matches LandingPage */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-surface-1 p-4 flex items-center justify-between h-[73px]">
        <Link to="/" className="flex items-center space-x-3">
          <IconDaggerheart className="w-8 h-8 text-red-400" />
          <span className="text-2xl font-bold text-gray-100 tracking-wider font-serif">
            Chaggerheart
          </span>
        </Link>
        <Link to="/" className="text-gray-400 hover:text-red-400 transition-colors text-sm">
          &larr; Back to Home
        </Link>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 pt-24 pb-12">
        <h1 className="text-4xl font-bold mb-2">Our Vision</h1>
        <p className="text-gray-400 mb-8">Chag Innovations LLC</p>

        <div className="prose prose-invert max-w-none space-y-8">

          {/* The Problem */}
          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">The Problem</h2>
            <p className="text-gray-300">
              People play tabletop RPGs for the same reason they do improv and creative writing: to inhabit
              someone else for a few hours. A shy accountant becomes a silver-tongued bard. A stressed-out
              nurse becomes a guardian standing between her friends and a god. The dice add stakes, and for a
              few hours the real world recedes.
            </p>
            <p className="text-gray-300 mt-3">
              Then someone has to look up a modifier. A player stops mid-scene to check what their ability does.
              The GM pauses to calculate armor. The spell breaks. After the session, the GM spends hours writing
              notes, updating characters, and reconstructing what happened so the next session can pick up where
              this one left off.
            </p>
            <p className="text-gray-300 mt-3">
              Most campaigns don't die from bad stories. They die from the logistics between them.
              Daggerheart was designed to strip mechanical overhead so players can stay in character. But the
              tooling never caught up. Characters still live in fillable PDFs. The game made space for immersion.
              The tools keep interrupting it.
            </p>
          </section>

          {/* The Solution */}
          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">The Solution</h2>
            <p className="text-gray-300">
              Chaggerheart picks up where the game design left off. It's a web app built around a single
              principle: automation frees you to play.
            </p>
            <p className="text-gray-300 mt-3">
              Everything that pulls a player out of character, the app handles. Abilities resolve on tap.
              Dice rolls apply their mechanical effects automatically. Character sheets live on the phone in
              your hand at the table, not in a binder you flip through. For GMs, the dashboard shows the whole
              party's state in real time, so running a scene never means breaking it to ask "wait, how much HP
              do you have?"
            </p>
          </section>

          {/* The Campaign Chronicler */}
          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">The Campaign Chronicler</h2>
            <p className="text-gray-300">
              The biggest intervention is after the session ends. Record your Discord session and get
              structured notes with separate GM and player perspectives. Speakers are identified, characters
              are recognized by name, and a "Previously On..." recap is ready for next session. A 3-hour
              session produces finished notes in seconds. The GM stays in creative control. The app handles
              everything that isn't storytelling.
            </p>
            <p className="text-gray-300 mt-3">
              <strong className="text-gray-100">Coming next:</strong> Long-term campaign memory that carries
              context across sessions, and scene illustrations woven into the notes.
            </p>
          </section>

          {/* The Homebrew Workshop */}
          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">The Homebrew Workshop</h2>
            <p className="text-gray-300">
              Create custom abilities and domain cards with a guided builder, live preview, and built-in
              balance checking that compares your creations against SRD mechanics. Your homebrew content
              works exactly like official content in the character sheet, with the same tap-to-activate
              interactions. No second-class citizens.
            </p>
          </section>

          {/* Campaign Frames */}
          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">Campaign Frames</h2>
            <p className="text-gray-300">
              14 interactive mechanics across 5 campaign frames. Beast Feast cooking with a dice-matching
              minigame. Witherwild corruption tracking. Day/night cycles. Faction relations and diplomacy.
              Colossal adversary encounters. Campaign frames reshape your game mechanics to fit your setting.
            </p>
          </section>

          {/* What We Ship */}
          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">What We Ship</h2>
            <p className="text-gray-300">
              189 domain cards. 36 ancestry features. 75 subclass features. 14 campaign frame mechanics.
              The full SRD, interactive. Built and shipped by a solo founder with no engineering team.
            </p>
            <p className="text-gray-300 mt-3">
              Our testers have been playing since August 2025. Their feedback shapes every feature.
              If you're curious about the architecture and engineering decisions,
              there's a{' '}
              <a href="https://github.com/svivasgo/chag-innovations-portfolio/tree/main/projects/chaggerheart"
                 target="_blank" rel="noopener noreferrer"
                 className="text-red-400 hover:text-red-300 underline transition-colors">
                public write-up on GitHub
              </a>.
            </p>
          </section>

          {/* Join the Beta */}
          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">Join the Beta</h2>
            <p className="text-gray-300">
              Chaggerheart is in open beta. Sign in with Google to get started. No waitlist, no approval needed.
            </p>
            <ul className="list-disc list-inside text-gray-300 mt-3 space-y-1">
              <li>Full access to everything, free through beta</li>
              <li>Direct line to the dev team</li>
              <li>First access to every new feature</li>
            </ul>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-700/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center text-sm text-gray-500 mb-6 max-w-2xl mx-auto">
            <p className="mb-2">Compatible with Daggerheart.</p>
            <p>
              This application uses content from the Daggerheart System Reference Document
              under the Darrington Press Community Gaming License. Daggerheart and Darrington Press
              are trademarks of Darrington Press, LLC.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400 mb-6">
            <Link to="/about" className="hover:text-red-400 transition-colors">About</Link>
            <Link to="/privacy" className="hover:text-red-400 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-red-400 transition-colors">Terms of Service</Link>
          </div>
          <div className="text-center text-xs text-gray-600">
            &copy; 2025-2026 Chag Innovations LLC. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
